import React, { useState } from "react";
import { ArrowUpRight, RotateCw } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BUCKET_LABEL, finApi, money } from "@/lib/financeApi";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { Empty, ErrorBox, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { TxTable } from "@/components/nds/windows/finance/OutsideIncomeTab";

const H = ({ children }) => <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">{children}</h4>;

function BalancesTable({ rows }) {
  return (
    <table data-testid="fin-recap-balances" className="w-full text-left text-sm border border-white/10">
      <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted"><th className="px-3 py-2 font-normal">Bucket</th><th className="px-3 py-2 font-normal text-right">Org-level</th><th className="px-3 py-2 font-normal text-right">All (org + residents)</th></tr></thead>
      <tbody>{rows.map((b) => (
        <tr key={b.bucket} data-testid={`fin-recap-bal-${b.bucket}`} className="border-t border-white/5">
          <td className="px-3 py-1.5 text-nex-silver">{BUCKET_LABEL[b.bucket]}</td>
          {[b.org, b.all].map((v, i) => <td key={i} className={`px-3 py-1.5 text-right font-mono text-xs ${v < 0 ? "text-red-300" : "text-nex-silver"}`}>{money(v)}</td>)}
        </tr>))}</tbody>
    </table>
  );
}

function OpenItems({ items }) {
  const { openWindow } = useFloatingWindows();
  return (
    <ul data-testid="fin-recap-open-items" className="divide-y divide-white/5 border border-white/10">
      {items.map((x) => (
        <li key={x.key}><button type="button" onClick={() => openWindow(x.window, { title: x.label })} data-testid={`fin-recap-open-${x.key}`} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-nex-gold/5 transition-colors">
          <span className="text-sm text-nex-silver">{x.label}</span>
          <span className={`font-mono text-xs ${x.count ? "text-yellow-300" : "text-nex-muted"}`}>{x.count}</span>
          <span className="ml-auto font-mono text-xs text-nex-gold">{money(x.amount)}</span><ArrowUpRight className="h-3.5 w-3.5 text-nex-muted" />
        </button></li>
      ))}
    </ul>
  );
}

const SCOPES = [["all", "All time"], ["month", "Month"], ["week", "Week"]];

/** All time / Month / Week picker for the recap. */
function ScopeBar({ scope, setScope, value, setValue }) {
  const [opts] = useLoad(() => finApi.get("/flow/months"), []);
  const list = scope === "month" ? opts.data?.months || [] : (opts.data?.weeks || []).slice().reverse();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fin-recap-scope">
      {SCOPES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => { setScope(k); setValue(""); }} data-testid={`fin-recap-scope-${k}`}
          className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${scope === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{l}</button>))}
      {scope !== "all" && (
        <select value={value} onChange={(e) => setValue(e.target.value)} data-testid="fin-recap-period" className="[color-scheme:dark] bg-black/40 border border-white/10 text-nex-silver text-xs px-2 py-1.5 rounded-sm">
          <option value="">{scope === "month" ? "Latest month" : "Latest week"}</option>
          {(scope === "month" ? list.slice().reverse() : list).map((x) => <option key={x} value={x}>{scope === "week" ? `Week ending ${x}` : x}</option>)}
        </select>)}
    </div>
  );
}

export default function FinRecapContent() {
  const isSuper = useIsSuper();
  const [scope, setScope] = useState("all");
  const [value, setValue] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const params = scope === "month" ? { month: value || today.slice(0, 7) } : scope === "week" ? { week: value || today } : {};
  const [state, load] = useLoad(() => (isSuper ? finApi.get("/recap", params) : Promise.resolve(null)), [isSuper, scope, value]);
  if (!isSuper) return <Empty testid="fin-recap-forbidden">Only a Super Admin can view the finance recap.</Empty>;
  if (state.loading && !state.data) return <Loading testid="fin-recap-loading" label="Loading snapshot…" />;
  if (state.error) return <ErrorBox testid="fin-recap-error" error={state.error} onRetry={load} />;
  if (!state.data) return null;
  const d = state.data;
  const trend = d.trend.map((w) => ({ ...w, label: d.step === "month" ? w.week_start.slice(0, 7) : w.week_start.slice(5) }));
  const per = { all: "All time", month: d.label, week: `Week ${d.date_from} → ${d.date_to}` }[d.scope];
  return (
    <div data-testid="fin-recap-content" className={`h-full overflow-y-auto p-5 space-y-6 transition-opacity ${state.loading ? "opacity-50" : ""}`}>
      <ScopeBar scope={scope} setScope={setScope} value={value} setValue={setValue} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.03]"><div className="nds-label-dim text-[9px]">Period</div><div className="text-sm text-nex-silver" data-testid="fin-recap-label">{per}</div></div>
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.03]"><div className="nds-label-dim text-[9px]">Inflows</div><div className="font-mono text-emerald-300" data-testid="fin-recap-inflow">{money(d.totals.inflow)}</div></div>
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.03]"><div className="nds-label-dim text-[9px]">Outflows</div><div className="font-mono text-red-300" data-testid="fin-recap-outflow">{money(d.totals.outflow)}</div></div>
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.03]"><div className="nds-label-dim text-[9px]">Internal moves · no cash</div><div className="font-mono text-sky-200" data-testid="fin-recap-internal">{money(d.totals.internal)}</div></div>
      </div>
      <div className="flex items-center"><span data-testid="fin-recap-asof" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Balances as of {d.as_of}</span>
        <button type="button" onClick={load} data-testid="fin-recap-refresh" aria-label="Refresh" className="ml-auto p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button></div>
      <section className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2"><H>Balances by bucket</H><BalancesTable rows={d.balances} /></div>
        <div className="space-y-2"><H>Open items</H><OpenItems items={d.open_items} /></div>
      </section>
      <section className="space-y-2" data-testid="fin-recap-trend">
        <H>Inflows vs outflows per {d.step}</H>
        <div className="h-56 border border-white/10 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend}><CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#8a8f98", fontSize: 10 }} /><YAxis tick={{ fill: "#8a8f98", fontSize: 10 }} width={60} />
              <Tooltip formatter={(v) => money(v)} labelFormatter={(l, p) => (p?.[0] ? `${p[0].payload.week_start} → ${p[0].payload.week_end}` : l)} contentStyle={{ background: "#0b0d10", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="inflow" name="Inflows" fill="#34d399" radius={[2, 2, 0, 0]} /><Bar dataKey="outflow" name="Outflows" fill="#f87171" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section className="space-y-2"><H>{d.scope === "all" ? "Recent transactions · last 20" : "Latest 20 transactions in this period"}</H>
        {d.recent.length ? <TxTable testid="fin-recap-recent" items={d.recent} /> : <Empty testid="fin-recap-recent-empty">No ledger activity yet.</Empty>}
      </section>
    </div>
  );
}
